// The robot's board on the network: the browser holds the sockets, the
// emulated firmware holds the protocol.
//
// The ESP32-S3's radio cannot be emulated at the register level -- its MAC is
// driven by a binary blob against undocumented registers -- so `esp32rs`
// answers the Arduino `WiFi` class and lwIP's socket calls from outside
// instead. Everything above them still executes on the emulated Xtensa: the
// sketch, its WebSocket library, the HTTP upgrade it writes and the frames it
// encodes are all real code running real instructions. What arrives here is
// only what the emulator could not do for itself.
//
// It arrives as *messages* rather than as a byte stream, which is what makes
// this possible at all. A page can open a `WebSocket` and cannot open a raw
// TCP socket, so the emulator terminates the protocol on its own side -- it
// answers the sketch's handshake with a real `101` and unwraps its frames --
// and hands over what the frames carried. So the far end is an ordinary
// WebSocket server with nothing in front of it: `../ws-server` needs no proxy
// and no change.
//
// The address is the sketch's own. Whatever host and port it passed to
// `webSocket.begin`, and whatever path it asked for in the upgrade, is what
// gets dialled here -- so a sketch pointed at a machine this browser cannot
// reach fails, exactly as the board would. Two consequences worth knowing
// when one does not connect:
//
//   - `ws-server` binds 127.0.0.1:9001 unless told otherwise, so a sketch
//     naming a LAN address needs `cargo run -- 0.0.0.0:9001` over there.
//   - a page served over https cannot open a `ws://` socket at all; the
//     browser blocks it as mixed content. The dev server is http, which is
//     why this works there.

import { AddedRobot, InterfaceHybrid } from "simulator";

// How often the board is asked what it wants of the network. Between the
// serial monitor's 100 ms and the switch panel's 250 ms: this carries a
// sketch's traffic, but a sketch sending faster than this would be sending
// faster than the emulated chip can produce anyway.
const POLL_MS = 50;

// What `net_request_kind` means.
const OPEN = 0;
const SEND = 1;
const CLOSE = 2;

let hybrid: InterfaceHybrid | undefined;
// Which articulated's board is on the network. As with the editor, the serial
// monitor and the switch panel, that is the most recently added robot.
let robot = 0;
let timer: number | undefined;
// Live connections by the id the emulator knows them as. Ids are never reused,
// including across a reboot, so a stale one cannot collect a fresh socket's
// events.
const sockets = new Map<number, WebSocket>();

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Put the board of the robot just added on the network.
export function connectNetworkOf(scene: InterfaceHybrid, added: AddedRobot) {
  // Drop whatever the previous robot had open. Its board is still in the
  // scene, but nothing polls it any more, so a socket left connected would
  // hold a server's connection open with nobody reading it.
  closeAll();

  hybrid = scene;
  robot = added.index;

  if (timer === undefined) {
    timer = window.setInterval(poll, POLL_MS);
  }
}

// Close every connection and forget it. Called when the panel is re-pointed
// at another robot, and when polling gives up.
function closeAll() {
  for (const socket of sockets.values()) {
    // The handlers check `sockets` before reporting anything, so closing here
    // cannot deliver an event for a robot that is no longer being polled.
    socket.close();
  }
  sockets.clear();
}

function poll() {
  if (!hybrid) {
    return;
  }

  let count: number;
  try {
    count = hybrid.net_take_requests_at(robot);
  } catch (error) {
    // wasm is unusable after a panic, so one crash would otherwise flood the
    // console twenty times a second.
    window.clearInterval(timer);
    timer = undefined;
    closeAll();
    console.error("network: stopped polling", error);
    return;
  }

  for (let n = 0; n < count; n++) {
    const id = hybrid.net_request_id(n);
    switch (hybrid.net_request_kind(n)) {
      case OPEN:
        open(id, hybrid.net_request_host(n), hybrid.net_request_port(n), hybrid.net_request_path(n));
        break;
      case SEND: {
        const socket = sockets.get(id);
        const data = hybrid.net_request_data(n);
        if (socket && socket.readyState === WebSocket.OPEN) {
          // A binary payload is copied once on the way out. What wasm hands
          // over is a view whose buffer `send` will not take as it stands,
          // and `slice` is what gives it one of exactly the right size.
          socket.send(hybrid.net_request_is_text(n) ? decoder.decode(data) : data.slice().buffer);
        }
        break;
      }
      case CLOSE:
        sockets.get(id)?.close();
        sockets.delete(id);
        break;
    }
  }
}

function open(id: number, host: string, port: number, path: string) {
  const url = `ws://${host}:${port}${path}`;
  let socket: WebSocket;
  try {
    socket = new WebSocket(url);
  } catch (error) {
    // A malformed address, or a scheme the page is not allowed to open.
    console.error(`network: cannot open ${url}`, error);
    hybrid?.net_closed_at(robot, id);
    return;
  }

  // Binary frames reach the sketch as bytes rather than as a Blob it would
  // have to wait on.
  socket.binaryType = "arraybuffer";
  sockets.set(id, socket);
  console.log(`network: the sketch is connecting to ${url}`);

  socket.onopen = () => {
    // Not before this. The firmware answers its own handshake on the strength
    // of it, and a client told it is connected when it is not writes into a
    // hole.
    if (sockets.get(id) === socket) {
      hybrid?.net_opened_at(robot, id);
    }
  };

  socket.onmessage = (event) => {
    if (sockets.get(id) !== socket) {
      return;
    }
    if (typeof event.data === "string") {
      hybrid?.net_message_at(robot, id, true, encoder.encode(event.data));
    } else {
      hybrid?.net_message_at(robot, id, false, new Uint8Array(event.data));
    }
  };

  // A refused connection reaches the sketch the same way a dropped one does:
  // there is nothing else a TCP client is told either. `onclose` fires after
  // `onerror` in both cases, so the report happens once, there.
  socket.onerror = () => console.warn(`network: ${url} failed`);

  socket.onclose = () => {
    if (sockets.get(id) !== socket) {
      return;
    }
    sockets.delete(id);
    hybrid?.net_closed_at(robot, id);
  };
}
